import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trackDemoBookingConversion, trackContactFormConversion } from '../../../utils/analytics';

export default function DemoBookingForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    teamSize: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d65etpv0ioc4omtkaagg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Track Google Ads conversion for demo booking
        trackDemoBookingConversion('demo-page-form');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          phone: '',
          teamSize: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="book-demo"
      className="py-28 bg-gradient-to-b from-slate-950 to-slate-900 relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-amber-500/[0.04] rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/15 mb-6">
              <i className="ri-calendar-check-line text-amber-400 text-sm" />
              <span className="text-amber-400 font-medium text-sm">Free 30-Minute Demo</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Get a Personalized
              <span className="block mt-1 bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                Walkthrough
              </span>
            </h2>
            <p className="text-lg text-slate-400 mb-10 leading-relaxed">
              Schedule a one-on-one demo with our team. We&apos;ll show you exactly how MtaPlannerPro fits
              your trade and workflow.
            </p>

            <div className="space-y-6 mb-10">
              {[
                {
                  icon: 'ri-user-star-line',
                  title: 'Tailored to Your Trade',
                  desc: 'Demo customized for HVAC, plumbing, electrical, or your specific trade',
                },
                {
                  icon: 'ri-question-answer-line',
                  title: 'Live Q&A Session',
                  desc: 'Ask anything — our construction software specialists have answers',
                },
                {
                  icon: 'ri-gift-2-line',
                  title: '30-Day Pro Trial',
                  desc: 'Demo attendees get extended Professional plan access free',
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <i className={`${item.icon} text-amber-400 text-xl`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-customer-service-2-line text-white text-xl" />
                </div>
                <div>
                  <h4 className="font-bold text-white">Need Help Now?</h4>
                  <p className="text-slate-400 text-xs">AI Assistant available 24/7</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                Chat with our AI for instant answers about features, pricing, and getting started.
              </p>
              <button
                onClick={() =>
                  document
                    .querySelector<HTMLButtonElement>('#vapi-widget-floating-button')
                    ?.click()
                }
                className="w-full py-3 bg-white/10 border border-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-message-3-line mr-2" />
                Start Chat
              </button>
            </div>
          </div>

          <div className="bg-white/[0.04] backdrop-blur-xl rounded-3xl border border-white/[0.08] shadow-2xl p-8 lg:p-10">
            <h3 className="text-2xl font-bold text-slate-900 mb-2 font-sora">
              Book Your MtaPlannerPro Demo
            </h3>
            <p className="text-slate-600 mb-8">
              See how MtaPlannerPro can transform your construction invoicing workflow. Schedule a personalized demo with our team.
            </p>

            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center mx-auto mb-6">
                  <i className="ri-check-line text-amber-400 text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Demo Request Received!</h3>
                <p className="text-slate-400 mb-6 text-sm">
                  We&apos;ll contact you shortly to schedule your personalized demo.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-amber-400 font-medium hover:text-amber-300 cursor-pointer text-sm"
                >
                  <i className="ri-arrow-left-line" /> Back to Home
                </Link>
              </div>
            ) : (
              <form
                id="demo-booking-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    Work Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                    placeholder="Acme Construction"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm"
                      placeholder="+61 400 000 000"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Team Size *
                    </label>
                    <select
                      name="teamSize"
                      value={formData.teamSize}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="" className="bg-slate-900">
                        Select...
                      </option>
                      <option value="1-5" className="bg-slate-900">
                        1-5 people
                      </option>
                      <option value="6-20" className="bg-slate-900">
                        6-20 people
                      </option>
                      <option value="21-50" className="bg-slate-900">
                        21-50 people
                      </option>
                      <option value="51-100" className="bg-slate-900">
                        51-100 people
                      </option>
                      <option value="100+" className="bg-slate-900">
                        100+ people
                      </option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1.5">
                    What interests you most?
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20 outline-none transition-all text-sm resize-none"
                    placeholder="Tell us about your construction business..."
                  />
                </div>

                {submitStatus === 'error' && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
                    Something went wrong. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white py-3.5 rounded-xl font-semibold hover:from-amber-600 hover:to-orange-700 hover:shadow-lg hover:shadow-amber-500/25 transition-all disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" /> Submitting...
                    </span>
                  ) : (
                    'Request Demo'
                  )}
                </button>

                <p className="text-[10px] text-slate-500 text-center">
                  By submitting, you agree to our{' '}
                  <a href="#" className="text-amber-400 hover:underline cursor-pointer">
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
